import {deleteCookie, getCookie, hasCookie, setCookie} from 'cookies-next';
import {jwtDecode} from 'jwt-decode';
import {IJwtPayload, ILoginBody} from '../interfaces';
import {EProfileType, ERegisterStatus} from '../enums';
import {QueryService} from './QueryService';

export const AuthService = {
    init: async function() {
        const isHasTestCookie = await hasCookie('testCookie');

        if (!isHasTestCookie && process.env.NEXT_PUBLIC_STANDALONE === 'true') {
            const uuid = self.crypto.randomUUID().split('-').slice(-1).join('').toString();
            const currentDate = new Date().toLocaleDateString().split('.').join('').toString();
            setCookie('testCookie', `${uuid}-${currentDate}`);
            return false;
        } else return true;
    },
    defineRole: function (array: string[]) {
        if (array.includes(EProfileType.Admin)) {
            return EProfileType.Admin as string
        } else if (array.includes(EProfileType.Buyer)) {
            return EProfileType.Buyer as string
        } else if (array.includes(EProfileType.Manufacturer)) {
            return EProfileType.Manufacturer as string
        } else if (array.includes(EProfileType.Distributer)) {
            return EProfileType.Distributer as string
        } else {
            return ''
        }
    },
    login: async function (body: ILoginBody) {
        try {
            const response = await fetch(`${process.env.NEXT_PUBLIC_DOMAIN_ID}/tkmp/oauth/token`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    username: body.email,
                    password: body.password,
                }),
                credentials: 'include',
            });

            if (response.ok) {
                const json = await response.json();

                const payload: any = jwtDecode<IJwtPayload>(json.accessToken);
                if (process.env.NEXT_PUBLIC_STANDALONE === 'true') {
                    setCookie('accessToken', json.accessToken);
                    setCookie('username', payload.sub);

                    await this.setAuthSSR(json)
                }
                if (process.env.NEXT_PUBLIC_DEBUG === 'true') console.log(payload.realm_access.roles);
                if (AuthService.defineRole(payload.realm_access.roles) !== '') {
                    if (process.env.NEXT_PUBLIC_STANDALONE === 'true')
                        {
                            setCookie('userrole', this.defineRole(payload.realm_access.roles) as EProfileType);
                        }
                    // router.push('/profile/account/user');
                    return {
                        name: payload.sub,
                        role: AuthService.defineRole(payload.realm_access.roles) as EProfileType,
                        status: true,
                        expired: Number(`${payload.exp}000`) ?? Date.now(),
                    };
                } else {
                    if (process.env.NEXT_PUBLIC_STANDALONE === 'true')
                        setCookie('userrole', EProfileType.Unknown);
                    return {
                        name: '',
                        role: EProfileType.Unknown,
                        status: false,
                        expired: Number(`${payload.exp}000`) ?? Date.now(),
                    };
                }
            } else {
                return {name: '', role: EProfileType.Unknown, status: false, expired: Date.now()};
            }
        } catch (error) {
            console.error(error);
            return {name: '', role: EProfileType.Unknown, status: false, expired: Date.now()};
        }
    },
    logout: async function () {
        return await new Promise(async (resolve, reject) => {
            try {
                if (process.env.NEXT_PUBLIC_STANDALONE === 'true') {
                    this.cookieKeys.forEach((key) => {
                        deleteCookie(key)
                    })

                    if (!(this.cookieKeys.some(key => hasCookie(key)))) {
                        resolve(true)
                    } else {
                        reject(false)
                    }
                }
                else {
                    const token = getCookie('accessToken');
                    return await QueryService.request(`${process.env.NEXT_PUBLIC_DOMAIN_ID}/tkmp/oauth/logout`, {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }).then((_)=>{
                        this.cookieKeys.forEach((key) => {
                            deleteCookie(key)
                        })
                        if (!(this.cookieKeys.some(key => hasCookie(key)))) {
                            resolve(true)
                        } else {
                            reject(false)
                        }
                    });
                }
            } catch (error: any) {
                throw new Error(error)
            }
        })
    },
    register: async function (body: any) {
        const response = await fetch(`${process.env.NEXT_PUBLIC_DOMAIN_ID}/tkmp/register`, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.ok) {
            const json = await response.json();

            if (!json.user_created) {
                return {status: ERegisterStatus.Failed, value: json.error_message};
            } else {
                setCookie('register_token', json.debug_token ?? '');

                return {status: ERegisterStatus.Success, value: json};
            }
        } else {
            return {status: ERegisterStatus.Bad, value: response.status.toString()};
        }
    },
    validateAuthSSR: function (cookies: any) {``
        const username = cookies?.username;
        const userrole = cookies?.userrole;
        const token = cookies?.token;

        if (userrole && username && token) {
            const payload: IJwtPayload = jwtDecode<IJwtPayload>(token);
            const tokenExpiredDate = new Date(Number(`${payload.exp}000`)).toJSON();
            const currentDate = new Date().toJSON();

            return currentDate <= tokenExpiredDate;
        }
        return false;
    },
    validateAuthCSR: function (cookies: { username: any; userrole: any; token: any }) {
        if (cookies.userrole && cookies.username && cookies.token) {
            const payload: IJwtPayload = jwtDecode<IJwtPayload>(cookies.token);
            const tokenExpiredDate = new Date(Number(`${payload.exp}000`)).toJSON();
            const currentDate = new Date().toJSON();

            return currentDate <= tokenExpiredDate;
        }
    },
    validateAuth: async function () {
        try {
            const username = await getCookie('username');
            const userrole = await getCookie('userrole');
            const token = await getCookie('accessToken');

            if (userrole && username && token) {
                const payload: IJwtPayload = jwtDecode<IJwtPayload>(token);
                const tokenExpiredDate = new Date(Number(`${payload.exp}000`)).toJSON();
                const currentDate = new Date().toJSON();

                return currentDate <= tokenExpiredDate;
            }
            return false;
        } catch (error) {
            console.error(error);
            return false;
        }
    },
    getCredentials: async function (locale:string = '') {
        const token = await getCookie('accessToken');

        if (token) {
            return await QueryService.request(
                `${process.env.NEXT_PUBLIC_DOMAIN_BFF}/tkmp/v1/${locale}/profile/account/user`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
        } else return null;
    },
    setAuth: function (data: any) {
        const jwt: any = jwtDecode(data.accessToken)
        setCookie('role', this.defineRole(jwt?.realm_access?.roles))
        setCookie('accessToken', data?.accessToken ?? '')
        setCookie('accessToken', data?.accessToken)
        setCookie('refreshToken', data?.refreshToken)
        setCookie('refreshExpiresIn', jwt.exp + data?.expiresIn)
        setCookie('expiresIn', jwt.exp + data?.refreshExpiresIn)
    },
    setAuthSSR: async function (data: any) {
        const jwt: any = await jwtDecode(data.accessToken)
        await setCookie('accessToken', data?.accessToken ?? '')
        await setCookie('role', this.defineRole(jwt?.realm_access?.roles))
        await setCookie('accessToken', data?.accessToken)
        await setCookie('refreshToken', data?.refreshToken)
        await setCookie('refreshExpiresIn', jwt.exp + data?.expiresIn)
        await setCookie('expiresIn', jwt.exp + data?.refreshExpiresIn)
    },
    checkCredentials: async function () {
        try {
            const missingKeys = this.cookieKeys.filter(key => !hasCookie(key));
            if (missingKeys.length) {
                console.log("Отсутствуют cookies:", missingKeys);
                return false;
            }
            return true;
        } catch (e) {
            console.error("Ошибка при проверке credentials:", e);
            return false;
        }
    },
    checkCredentialsSSR: async function (context: any) {
        try {
            const missingKeys = this.cookieKeys.filter(key => !context?.req?.cookies[key]);
            if (missingKeys.length) {
                console.log("Отсутствуют cookies (SSR):", missingKeys);
                return false;
            }
            return true;
        } catch (e) {
            console.error("Ошибка при проверке credentials (SSR):", e);
            return false;
        }
    },
    checkRefresh: async function() {
        return await new Promise(async (resolve, reject) => {
            try {
                if (await this.checkCredentials()) {
                    const currentDate = new Date().valueOf()
                    const logoutDate = parseInt(`${parseInt(getCookie('refreshExpiresIn') as string)}000`)
                    const logoutDateRefresh = parseInt(`${parseInt(getCookie('expiresIn') as string)}000`)

                    if (logoutDate < currentDate && currentDate < logoutDateRefresh) {
                        const response = await fetch(`${process.env.NEXT_PUBLIC_DOMAIN_ID}/tkmp/oauth/refresh`, {
                            method: "POST",
                            body: JSON.stringify({
                                refreshToken: getCookie('refreshToken'),
                            }),
                            headers: {
                                Authorization: `Bearer ${getCookie('accessToken')}`,
                                'Content-Type': 'application/json',
                            }
                        })

                        if (!response.ok) {
                            // throw new Error(response.statusText);
                            console.error(response)
                            resolve(false)
                        } else {
                            const data = await response.json();
                            this.setAuth(data)
                            resolve(true)
                        }
                    } else if (currentDate > logoutDateRefresh) {
                        resolve(false)
                    } else {
                        resolve(true)
                    }
                } else {
                    resolve(false)
                }
            } catch (error: any) {
                resolve(false)
                throw new Error(error)
            }
        })
    },
    checkRefreshSSR: async function(context: any) {
        return await new Promise(async (resolve, reject) => {
            try {
                if (await this.checkCredentialsSSR(context)) {
                    const cookies = context?.req?.cookies
                    const currentDate = new Date().valueOf()
                    const logoutDate = parseInt(`${parseInt(cookies.logoutDate as string)}000`)
                    const logoutDateRefresh = parseInt(`${parseInt(cookies.logoutDateRefresh as string)}000`)

                    if (logoutDate < currentDate && currentDate < logoutDateRefresh) {
                        const response = await fetch(`${process.env.NEXT_PUBLIC_DOMAIN_ID}/tkmp/oauth/refresh`, {
                            method: "POST",
                            body: JSON.stringify({
                                refreshToken: cookies.refreshToken,
                            }),
                            headers: {
                                Authorization: `Bearer ${cookies.accessToken}`,
                                'Content-Type': 'application/json',
                            }
                        })

                        if (!response.ok) {
                            // throw new Error(response.statusText);
                            console.error(response)
                            resolve(false)
                        } else {
                            const data = await response.json();
                            this.setAuth(data)
                            resolve(true)
                        }
                    } else if (currentDate > logoutDateRefresh) {
                        resolve(false)
                    } else {
                        resolve(true)
                    }
                } else {
                    resolve(false)
                }
            } catch (error: any) {
                reject(false)
                throw new Error(error)
            }
        })
    },
    notAuthorized: {
        props: {
            isAuth: false
        }
    },
    cookieKeys: ['accessToken', 'refreshToken', 'refreshExpiresIn', 'expiresIn', 'userrole', 'username']
};